import * as THREE from 'three';

/**
 * Hair and beards drawn as strands in the pixel shader, so they read the same at every Character detail level: the coarse world
 * meshes (a few hundred vertices) and the close-up head share one look, since the strands do not come from the geometry.
 *
 * Each hair vertex carries three attributes (see `HeadPiece` in `human-head.ts`): `hairFlow`, the direction its strands run (away from
 * the crown, toward a tie, down the chin); `hairCover`, how fully the hair hides the skin, from 0 at the hairline or the beard's edge to
 * 1 where it is thick; and `hairAt`, where it lies among the strands, as a point on a circle round the hair's upright axis plus a height,
 * so strands keep their place however coarse the mesh is and have no seam. The fragment shader lays clumps and fine strands by that
 * angle, wavering along their length, and fades each to its mean before it would shimmer at the current size. Where cover is thin the
 * skin shows between the strands, so hairlines and beard edges thin out instead of ending on a cut. A Kajiya-Kay highlight runs across
 * the strands under each directional light: a soft, dull sheen (art direction: matte, not glossy).
 */
export const HAIR_FLOW = 'hairFlow';
export const HAIR_COVER = 'hairCover';
export const HAIR_AT = 'hairAt';

export interface HairShadingOptions {
  /** The skin the hair grows from, shown where the hair thins out. */
  skin: THREE.ColorRepresentation;
  /** How strong the sheen is, 0 for none. */
  shine?: number;
}

const COMMON = /* glsl */`
float hairHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float hairNoise(vec2 p) {
  vec2 i = floor(p), f = fract(p); f = f * f * (3. - 2. * f);
  return mix(mix(hairHash(i), hairHash(i + vec2(1., 0.)), f.x), mix(hairHash(i + vec2(0., 1.)), hairHash(i + vec2(1.)), f.x), f.y);
}
`;

/** Adds strand shading to the hair material; it keeps any shader changes already made to it (the sway). */
export function applyHairShading(material: THREE.MeshStandardMaterial, options: HairShadingOptions) {
  const skin = { value: new THREE.Color(options.skin) }, shine = { value: options.shine ?? .035 };
  const before = material.onBeforeCompile.bind(material), key = material.customProgramCacheKey.bind(material);
  material.customProgramCacheKey = () => `${key()}|hair-strands`;
  material.onBeforeCompile = (shader, renderer) => {
    before(shader, renderer);
    shader.uniforms.uHairSkin = skin; shader.uniforms.uHairShine = shine;
    shader.vertexShader = `attribute vec3 ${HAIR_FLOW};\nattribute float ${HAIR_COVER};\nattribute vec3 ${HAIR_AT};\nvarying vec3 vHairAt;\nvarying vec3 vHairFlowView;\nvarying float vHairCover;\n`
      + shader.vertexShader
        .replace('#include <skinnormal_vertex>', `#include <skinnormal_vertex>
        vHairAt = ${HAIR_AT}; vHairCover = ${HAIR_COVER};
        #ifdef USE_SKINNING
          vHairFlowView = normalMatrix * (skinMatrix * vec4(${HAIR_FLOW}, 0.)).xyz;
        #else
          vHairFlowView = normalMatrix * ${HAIR_FLOW};
        #endif`);
    shader.fragmentShader = `uniform vec3 uHairSkin;\nuniform float uHairShine;\nvarying vec3 vHairAt;\nvarying vec3 vHairFlowView;\nvarying float vHairCover;\n${COMMON}`
      + shader.fragmentShader
        .replace('#include <color_fragment>', `#include <color_fragment>
        // Clumps about 6 mm wide and strands about 1.2 mm, by their place round the axis, each wavering a little along its length;
        // each fades to its mean before it would alias.
        vec2 hairRound = vHairAt.xy * 170.;
        vec2 clumpAt = vec2(hairRound.x + hairNoise(vec2(vHairAt.z * 30., 0.)) * 1.2, hairRound.y), fineAt = hairRound * 5.2 + vec2(0., vHairAt.z * 40.);
        float clumpSeen = 1. - smoothstep(.35, .9, length(fwidth(hairRound))), fineSeen = 1. - smoothstep(.35, .9, length(fwidth(fineAt)));
        float hairStrand = mix(.5, hairNoise(clumpAt), clumpSeen) * .55 + mix(.5, hairNoise(fineAt), fineSeen) * .45;
        diffuseColor.rgb *= .62 + .75 * hairStrand;
        // Thin hair shows the skin between its strands.
        float hairShown = smoothstep(0., .32, clamp(vHairCover, 0., 1.) * 1.3 - (1. - hairStrand) * .5);
        diffuseColor.rgb = mix(uHairSkin, diffuseColor.rgb, hairShown);`)
        .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
        vec3 hairTangent = normalize(vHairFlowView - normal * dot(vHairFlowView, normal) + 1e-6);
        normal = normalize(normal + cross(normal, hairTangent) * (hairStrand - .5) * .45 * max(clumpSeen, fineSeen) * hairShown);`)
        .replace('#include <lights_fragment_end>', `#include <lights_fragment_end>
        #if NUM_DIR_LIGHTS > 0
        for (int i = 0; i < NUM_DIR_LIGHTS; i++) {
          vec3 hairL = directionalLights[i].direction, hairH = normalize(hairL + geometryViewDir);
          float hairTH = dot(hairTangent, hairH), hairSin = sqrt(max(0., 1. - hairTH * hairTH));
          float hairLit = smoothstep(-.05, .35, dot(normal, hairL));
          reflectedLight.directSpecular += directionalLights[i].color * mix(vec3(1.), diffuseColor.rgb, .5) * pow(hairSin, 24.) * hairLit * uHairShine * (.35 + .65 * hairStrand) * hairShown;
        }
        #endif`);
  };
  material.needsUpdate = true;
}
